import React, { useMemo, useState } from 'react';
import { Box, IconButton, Modal, Typography, useTheme, Menu, MenuItem } from '@mui/material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import OpenInFullIcon from 'packages/novous_web/src/assets/icons/Expand.svg';
import EditIcon from 'packages/novous_web/src/assets/icons/edit.svg';
import RestartAltIcon from 'packages/novous_web/src/assets/icons/Reset.svg';
import CollapseIcon from 'packages/novous_web/src/assets/icons/CollapseIcon.svg';
import InfoIcon from 'packages/novous_web/src/assets/icons/infoIcon.svg';
import CommonAnalysisTable from './CommonAnalysisTable';
import EditDataModal from './EditDataModal';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import dayjs from 'dayjs';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';

interface DayNightData {
  record_date?: string;
  vehicle_id?: string;
  day_drving_hours: number;
  night_driving_hours: number;
  total_driving_hours: number;
}

interface DayNightFloatingGraphProps {
  title?: string;
  data?: DayNightData[];
  chartId?: number;
  onCardDataUpdate?: (chartId?: number, startDate?: string, endDate?: string) => void;
  infoIcon?: React.ReactNode;
  alertColorMapping?: { [key: string]: string };
  tableColumns?: Array<{ key: string; label: string; align?: 'left' | 'center' | 'right' }>;
  tableData?: any[];
  tableTitle?: string;
  tableHeaderText?: string;
  paginationInfo?: any;
  onTableIconClick?: () => void;
  onPaginationChange?: (cursor: any) => void;
}

const DayNightFloatingGraph: React.FC<DayNightFloatingGraphProps> = ({
  title = 'Daily Vehicle Usage (Day vs Night)',
  data = [],
  chartId,
  onCardDataUpdate,
  infoIcon,
  alertColorMapping,
  tableColumns = [],
  tableData = [],
  tableTitle = 'Analysis Table',
  tableHeaderText = 'Detailed information',
  paginationInfo,
  onTableIconClick,
  onPaginationChange,
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [isExpandedModalOpen, setIsExpandedModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    console.log('Expand clicked for:', title);
    setIsExpandedModalOpen(true);
    handleMenuClose();
  };

  const handleEditData = () => {
    console.log('Edit Data clicked for:', title);
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (dateData: any) => {
    console.log('Applied edit data:', dateData);
    if (onCardDataUpdate && chartId) {
      onCardDataUpdate(chartId, dateData.startDate, dateData.endDate);
      const startDateFormatted = new Date(dateData.startDate).toLocaleDateString('en-GB');
      const endDateFormatted = new Date(dateData.endDate).toLocaleDateString('en-GB');
      showToast(`Date range applied: ${startDateFormatted} to ${endDateFormatted}`, 'success');
    }
  };

  const handleResetChanges = () => {
    console.log('Reset Changes clicked for:', title);
    handleMenuClose();
    setIsEditModalOpen(false);
    setResetTrigger((prev) => prev + 1);
    if (onCardDataUpdate && chartId) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onCardDataUpdate(chartId, startDate, endDate);
    }
  };

  const handleCloseExpandedModal = () => {
    setIsExpandedModalOpen(false);
  };

  const defaultColors = [
    '#5547DD', // Hours (Total Hours) - Purple/Blue
    '#27AE60', // Day Hours - Green
    '#F2994A', // Night Hours - Orange
  ];

  // Clean and validate data - Same as your original
  const cleanedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item) => {
      const dayHours = item.day_drving_hours || 0;
      const nightHours = item.night_driving_hours || 0;
      const totalHours = item.total_driving_hours || 0;

      const calculatedTotal = dayHours + nightHours;
      const validTotal = totalHours > 0 ? totalHours : calculatedTotal;

      return {
        record_date: item.record_date || '',
        vehicle_id: item.vehicle_id || '',
        day_drving_hours: dayHours,
        night_driving_hours: nightHours,
        total_driving_hours: validTotal,
      };
    });
  }, [data]);

  // Transform data for the chart
  const chartData = useMemo(() => {
    if (!cleanedData || cleanedData.length === 0) return [];

    // Determine whether to sort by date or vehicle_id
    let sortedData;
    if (cleanedData[0]?.vehicle_id) {
      // Use vehicle_id for x-axis (for Driving Hours Breakdown)
      sortedData = [...cleanedData];
    } else {
      // Use record_date for x-axis (for Daily Vehicle Usage)
      sortedData = [...cleanedData].sort(
        (a, b) => new Date(a.record_date || '').getTime() - new Date(b.record_date || '').getTime(),
      );
    }

    return sortedData.map((item) => {
      // Determine the x-axis label: use vehicle_id if available, otherwise use date
      let xAxisLabel = '';
      if (item.vehicle_id) {
        xAxisLabel = item.vehicle_id;
      } else if (item.record_date) {
        const date = new Date(item.record_date);
        xAxisLabel = `${date.getDate().toString().padStart(2, '0')} ${date.toLocaleString('default', {
          month: 'short',
        })}`;
      }

      return {
        fullDate: item.record_date || item.vehicle_id || '',
        date: xAxisLabel,
        'Day Hours': parseFloat(item.day_drving_hours.toFixed(2)),
        'Night Hours': parseFloat(item.night_driving_hours.toFixed(2)),
        'Total Hours': parseFloat(item.total_driving_hours.toFixed(2)),
      };
    });
  }, [cleanedData]);

  // Custom Tooltip - Shows both date and vehicle ID when available
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);
      const currentData = payload[0]?.payload;
      const fullDate = currentData?.fullDate || label;

      // Check if this is vehicle_id data or date data
      const isVehicleIdData = cleanedData[0]?.vehicle_id;
      let headerLabel = '';

      if (isVehicleIdData) {
        headerLabel = fullDate; // Display vehicle ID directly
      } else {
        headerLabel = dayjs(fullDate).format('DD MMM YYYY'); // Display formatted date
      }

      const isDailyVehicleUsage = title?.toLowerCase().includes('daily vehicle usage');

      return (
        <Box className="bg-white p-3 border rounded shadow-lg">
          <Typography
            variant="body2"
            fontSize={12}
            sx={(theme) => ({
              color: theme.palette.text.primary,
              fontWeight: 600,
            })}
          >
            {isVehicleIdData ? 'Vehicle ID: ' : ''}
            <strong>{headerLabel}</strong>
          </Typography>

          {payload.map((entry: any, index: number) => {
            // Update "Total Hours" to "Hours" for Daily Vehicle Usage chart
            const displayName = isDailyVehicleUsage && entry.name === 'Total Hours' ? 'Hours' : entry.name;
            return (
              <Box key={index} className="flex items-center mb-1">
                <Box className="w-3 h-3 mr-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <Typography
                  variant="body2"
                  fontSize={12}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                  })}
                >
                  {displayName}: <strong>{entry.value?.toFixed(2)}h</strong>
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }
    return null;
  };

  // Legend - EXACTLY like CommonFloatingGraph
  const renderCustomizedLegend = () => {
    // Determine which labels to use based on the title
    const isDrivingHoursBreakdown = title?.toLowerCase().includes('driving hours breakdown');

    const categories = isDrivingHoursBreakdown
      ? [
          { name: 'Total Hours', key: 'Total Hours', color: defaultColors[0] },
          { name: 'Low Speed Working Hours', key: 'Day Hours', color: defaultColors[1] },
          { name: 'Eco Driving Hours', key: 'Night Hours', color: defaultColors[2] },
        ]
      : [
          { name: 'Hours', key: 'Total Hours', color: defaultColors[0] },
          { name: 'Day Hours', key: 'Day Hours', color: defaultColors[1] },
          { name: 'Night Hours', key: 'Night Hours', color: defaultColors[2] },
        ];

    return (
      <Box className="flex flex-wrap gap-4 justify-end w-full mt-2">
        {categories.map((category) => (
          <Box key={category.key} className="flex items-center">
            <Box
              className="mr-2 rounded-full"
              style={{
                backgroundColor: category.color,
                width: '20px',
                height: '20px',
                opacity: 1,
              }}
            />
            <Typography
              variant="h6"
              fontSize={14}
              sx={{ color: theme.palette.text.primary, fontWeight: 400 }}
            >
              {category.name}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Function to get table data
  const getTableData = () => {
    if (!cleanedData || cleanedData.length === 0) return [];

    return cleanedData.map((item) => {
      const dayHours = parseFloat(item.day_drving_hours.toFixed(2));
      const nightHours = parseFloat(item.night_driving_hours.toFixed(2));
      const totalHours = parseFloat(item.total_driving_hours.toFixed(2));
      const dayPercentage = totalHours > 0 ? ((dayHours / totalHours) * 100).toFixed(1) : '0.0';
      const nightPercentage = totalHours > 0 ? ((nightHours / totalHours) * 100).toFixed(1) : '0.0';

      return {
        date: item.record_date,
        dayHours: dayHours,
        nightHours: nightHours,
        totalHours: totalHours,
        dayPercentage: dayPercentage,
        nightPercentage: nightPercentage,
        difference: parseFloat((totalHours - (dayHours + nightHours)).toFixed(2)),
      };
    });
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (cleanedData.length === 0) return null;

    const totalDayHours = cleanedData.reduce((sum, item) => sum + item.day_drving_hours, 0);
    const totalNightHours = cleanedData.reduce((sum, item) => sum + item.night_driving_hours, 0);
    const totalAllHours = cleanedData.reduce((sum, item) => sum + item.total_driving_hours, 0);

    const avgDayHours = totalDayHours / cleanedData.length;
    const avgNightHours = totalNightHours / cleanedData.length;
    const avgTotalHours = totalAllHours / cleanedData.length;

    return {
      totalDayHours: parseFloat(totalDayHours.toFixed(2)),
      totalNightHours: parseFloat(totalNightHours.toFixed(2)),
      totalAllHours: parseFloat(totalAllHours.toFixed(2)),
      avgDayHours: parseFloat(avgDayHours.toFixed(2)),
      avgNightHours: parseFloat(avgNightHours.toFixed(2)),
      avgTotalHours: parseFloat(avgTotalHours.toFixed(2)),
      dayPercentage: totalAllHours > 0 ? ((totalDayHours / totalAllHours) * 100).toFixed(1) : '0.0',
      nightPercentage: totalAllHours > 0 ? ((totalNightHours / totalAllHours) * 100).toFixed(1) : '0.0',
    };
  }, [cleanedData]);

  return (
    <>
      {/* EXACT SAME STRUCTURE as CommonFloatingGraph */}
      <Box
        className="w-full h-full flex flex-col border rounded-lg p-4 items-start mt-2 bg-white"
        sx={{ gap: 1 }}
      >
        {/* Header - Exactly same */}
        <Box className="w-full flex justify-between items-center">
          <Box className="flex items-center" sx={{ gap: 1 }}>
            <Typography
              variant="h6"
              fontSize={18}
              sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
            >
              {title}
            </Typography>
            {infoIcon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                }}
              >
                {infoIcon}
              </Box>
            )}
          </Box>

          <Box className="flex items-center" sx={{ gap: 1 }}>
            <Box
              className="flex items-center cursor-pointer"
              onClick={() => {
                setIsTableOpen(true);
                if (onTableIconClick) {
                  onTableIconClick();
                }
              }}
              sx={{ pt: '10px', gap: 0.3 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                }}
              >
                <img src={eyeIcon} alt="Table" width={24} height={24} style={{ display: 'block' }} />
              </Box>
              <Typography
                variant="body2"
                fontSize={14}
                sx={(theme) => ({ color: theme.palette.primary.main })}
              >
                Table
              </Typography>
            </Box>
            <Box
              className="flex items-center cursor-pointer"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                handleMenuClick(e as any);
              }}
              sx={{ pt: '10px', gap: 0.3 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                }}
              >
                <img src={menuIcon} alt="Menu" width="24" height="24" style={{ display: 'block' }} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Legend - Exactly same */}
        {chartData.length > 0 && (
          <Box className="w-full flex flex-col justify-start items-start mb-2">
            {renderCustomizedLegend()}
          </Box>
        )}

        {/* Chart Container - EXACTLY SAME as CommonFloatingGraph (no h-80 here) */}
        <Box className="w-full h-80 flex">
          {chartData.length > 0 ? (
            <>
              <Typography
                variant="caption"
                sx={{
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: '18px',
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                {title?.toLowerCase().includes('daily vehicle usage') ? 'Average Hours' : 'Driving Hours'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 20, bottom: 5 }}>
                    <defs>
                      <linearGradient id="colorTotalHours" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#5547DD" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#5547DD" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorDayHours" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#27AE60" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#27AE60" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorNightHours" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F2994A" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#F2994A" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, dy: 10 }}
                      padding={{ left: 30, right: 30 }}
                    />
                    <YAxis
                      domain={['auto', 'auto']}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 14 }}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />

                    {/* Areas - Same styling as CommonFloatingGraph */}
                    {['Total Hours', 'Day Hours', 'Night Hours'].map((dataKey, index) => {
                      const color = defaultColors[index % defaultColors.length];
                      const gradientId =
                        dataKey === 'Total Hours'
                          ? 'colorTotalHours'
                          : dataKey === 'Day Hours'
                          ? 'colorDayHours'
                          : 'colorNightHours';

                      return (
                        <Area
                          key={dataKey}
                          type="monotone"
                          dataKey={dataKey}
                          stroke={color}
                          fill={`url(#${gradientId})`}
                          strokeWidth={1}
                          dot={false}
                          name={
                            title?.toLowerCase().includes('driving hours breakdown')
                              ? dataKey === 'Total Hours'
                                ? 'Total Hours'
                                : dataKey === 'Day Hours'
                                ? 'Low Speed Working Hours'
                                : 'Eco Driving Hours'
                              : dataKey
                          }
                        />
                      );
                    })}
                  </AreaChart>
                </ResponsiveContainer>
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    color: '#1B1B1B',
                    pt: 1,
                  }}
                >
                  {cleanedData[0]?.vehicle_id ? 'Vehicle ID' : 'Date'}
                </Typography>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
            </Box>
          )}
        </Box>
      </Box>

      {/* Modal - CommonAnalysisTable for Productivity data or custom table for legacy data */}
      <Modal
        open={isTableOpen}
        onClose={() => setIsTableOpen(false)}
        aria-labelledby="day-night-table-modal"
        aria-describedby="day-night-table-description"
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            height: '90%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {tableColumns && tableColumns.length > 0 ? (
            <CommonAnalysisTable
              title={tableTitle}
              headerText={tableHeaderText}
              columns={tableColumns}
              data={tableData}
              noDataMessage="No data available for this selection"
              onClose={() => setIsTableOpen(false)}
              paginationInfo={paginationInfo}
              onPaginationChange={onPaginationChange}
            />
          ) : (
            <>
              <Box className="flex justify-between items-center mb-3">
                <Typography variant="h6" fontWeight={600}>
                  Table View - {title}
                </Typography>
                <IconButton onClick={() => setIsTableOpen(false)}>×</IconButton>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 sticky top-0">
                      <th className="border p-3 text-left">Date</th>
                      <th className="border p-3 text-left">Day Hours</th>
                      <th className="border p-3 text-left">Day %</th>
                      <th className="border p-3 text-left">Night Hours</th>
                      <th className="border p-3 text-left">Night %</th>
                      <th className="border p-3 text-left">Total Hours</th>
                      <th className="border p-3 text-left">Day+Night</th>
                      <th className="border p-3 text-left">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getTableData().map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border p-3 font-medium">{row.date}</td>
                        <td className="border p-3">{row.dayHours.toFixed(2)}</td>
                        <td className="border p-3">{row.dayPercentage}%</td>
                        <td className="border p-3">{row.nightHours.toFixed(2)}</td>
                        <td className="border p-3">{row.nightPercentage}%</td>
                        <td className="border p-3 font-bold bg-blue-50">{row.totalHours.toFixed(2)}</td>
                        <td className="border p-3">{(row.dayHours + row.nightHours).toFixed(2)}</td>
                        <td className={`border p-3 ${row.difference !== 0 ? 'bg-yellow-50' : ''}`}>
                          {row.difference !== 0 ? row.difference.toFixed(2) : '0.00'}
                        </td>
                      </tr>
                    ))}

                    {/* Summary Row */}
                    {summaryStats && (
                      <tr className="bg-gray-100 font-bold">
                        <td className="border p-3">TOTAL</td>
                        <td className="border p-3">{summaryStats.totalDayHours.toFixed(2)}</td>
                        <td className="border p-3">{summaryStats.dayPercentage}%</td>
                        <td className="border p-3">{summaryStats.totalNightHours.toFixed(2)}</td>
                        <td className="border p-3">{summaryStats.nightPercentage}%</td>
                        <td className="border p-3 bg-blue-100">{summaryStats.totalAllHours.toFixed(2)}</td>
                        <td className="border p-3">
                          {(summaryStats.totalDayHours + summaryStats.totalNightHours).toFixed(2)}
                        </td>
                        <td className="border p-3">
                          {parseFloat(
                            (
                              summaryStats.totalAllHours -
                              (summaryStats.totalDayHours + summaryStats.totalNightHours)
                            ).toFixed(2),
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
        </MenuItem>
        <MenuItem onClick={handleEditData} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Edit Data</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleResetChanges}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
        >
          <img
            src={RestartAltIcon}
            alt="Reset"
            style={{
              width: 20,
              height: 20,
              filter:
                'invert(34%) sepia(68%) saturate(2449%) hue-rotate(338deg) brightness(91%) contrast(84%)',
            }}
          />
          <Typography sx={{ fontSize: '14px', color: '#DC3545' }}>Reset Changes</Typography>
        </MenuItem>
      </Menu>

      {/* Expanded Modal */}
      <Modal open={isExpandedModalOpen} onClose={handleCloseExpandedModal}>
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            height: '85%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                {title}
              </Typography>
              {infoIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>{infoIcon}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                onClick={handleEditData}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                >
                  Edit Chart
                </Typography>
              </Box>
              <Box
                onClick={handleCloseExpandedModal}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                >
                  Collapse
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Chart Content */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, mb: 2 }}>
            {cleanedData && cleanedData.length > 0 ? (
              <>
                <Box sx={{ display: 'flex', flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      textAlign: 'center',
                      fontWeight: 400,
                      fontSize: 12,
                      lineHeight: '18px',
                      letterSpacing: '0%',
                      color: '#1B1B1B',
                    }}
                  >
                    {title?.toLowerCase().includes('daily vehicle usage') ? 'Average Hours' : 'Driving Hours'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 30, bottom: 5 }}>
                        <defs>
                          <linearGradient id="colorTotalHoursExpanded" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#5547DD" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#5547DD" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorDayHoursExpanded" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#27AE60" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#27AE60" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorNightHoursExpanded" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F2994A" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#F2994A" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke="#e0e0e0" />
                        <XAxis
                          dataKey="date"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, dy: 10 }}
                          padding={{ left: 30, right: 30 }}
                        />
                        <YAxis
                          domain={['auto', 'auto']}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 14 }}
                          width={40}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />

                        {['Total Hours', 'Day Hours', 'Night Hours'].map((dataKey, index) => {
                          const color = defaultColors[index % defaultColors.length];
                          const gradientId =
                            dataKey === 'Total Hours'
                              ? 'colorTotalHoursExpanded'
                              : dataKey === 'Day Hours'
                              ? 'colorDayHoursExpanded'
                              : 'colorNightHoursExpanded';
                          return (
                            <Area
                              key={dataKey}
                              type="monotone"
                              dataKey={dataKey}
                              stroke={color}
                              fill={`url(#${gradientId})`}
                              strokeWidth={1}
                              dot={false}
                              name={
                                title?.toLowerCase().includes('driving hours breakdown')
                                  ? dataKey === 'Total Hours'
                                    ? 'Total Hours'
                                    : dataKey === 'Day Hours'
                                    ? 'Low Speed Working Hours'
                                    : 'Eco Driving Hours'
                                  : dataKey
                              }
                            />
                          );
                        })}
                      </AreaChart>
                    </ResponsiveContainer>
                    <Typography
                      sx={{
                        textAlign: 'center',
                        fontWeight: 400,
                        fontSize: 12,
                        lineHeight: '18px',
                        letterSpacing: '0%',
                        color: '#1B1B1B',
                        pt: 1,
                      }}
                    >
                      {cleanedData[0]?.vehicle_id ? 'Vehicle ID' : 'Date'}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  minHeight: '300px',
                  width: '100%',
                }}
              >
                <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Edit Data Modal */}
      <EditDataModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onApply={handleApplyEditData}
        cardTitle={title}
        chartId={chartId}
        resetTrigger={resetTrigger}
      />
    </>
  );
};

export default DayNightFloatingGraph;
